import { useMemo } from 'react';
import { MapPin, MonitorOff } from 'lucide-react';
import type { RouteItem } from '@/types/routeMap';
import { RouteStatList } from './RouteStatList';
import { YearSwitcher } from './YearSwitcher';
import { UnknownAirportsPanel } from './UnknownAirportsPanel';

interface RouteFallbackViewProps {
  routes: RouteItem[];
  unknownAirports: string[];
  year: number | 'all';
  yearOptions: number[];
  onYearChange: (year: number | 'all') => void;
  onEditAirport: (code: string) => void;
  webglUnsupported?: boolean;
}

export function RouteFallbackView({
  routes,
  unknownAirports,
  year,
  yearOptions,
  onYearChange,
  onEditAirport,
  webglUnsupported = false,
}: RouteFallbackViewProps) {
  const summary = useMemo(() => {
    const totalRoutes = routes.length;
    const totalCount = routes.reduce((s, r) => s + r.count, 0);
    const totalMinutes = routes.reduce((s, r) => s + (r.totalMinutes || 0), 0);
    const totalHours = Math.round(totalMinutes / 60);
    const airports = new Set<string>();
    for (const r of routes) {
      airports.add(r.departure);
      airports.add(r.arrival);
    }
    return { totalRoutes, totalCount, totalHours, airportCount: airports.size };
  }, [routes]);

  return (
    <div className="relative z-10 px-6 lg:px-8 pb-8 space-y-4">
      {webglUnsupported && (
        <div className="flex items-center gap-3 rounded-xl bg-amber-400/5 ring-1 ring-amber-400/20 px-4 py-3">
          <MonitorOff className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <div className="text-sm font-medium text-amber-200">您的设备不支持 WebGL</div>
            <div className="text-xs text-amber-300/70 mt-0.5">已切换至列表视图,数据完整可用</div>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-slate-950/60 backdrop-blur-xl ring-1 ring-white/10 p-4 space-y-4">
        <YearSwitcher value={year} options={yearOptions} onChange={onYearChange} />
        <div className="grid grid-cols-3 gap-2">
          <Stat label="航线" value={summary.totalRoutes} />
          <Stat label="航段" value={summary.totalCount} />
          <Stat label="小时" value={summary.totalHours} />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <MapPin className="w-3 h-3" />
          途经 <span className="text-slate-300">{summary.airportCount}</span> 个机场
          {unknownAirports.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20">
              {unknownAirports.length} 待补录
            </span>
          )}
        </div>
      </div>

      <RouteStatList routes={routes} topN={5} />
      <UnknownAirportsPanel codes={unknownAirports} onEdit={onEditAirport} />
    </div>
  );
}

interface StatProps {
  label: string;
  value: number;
}

function Stat({ label, value }: StatProps) {
  return (
    <div className="rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2">
      <div className="text-lg font-semibold text-slate-100 tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}
